import { Billing, CreateSubscriptionOutput, GetPlanOutput } from "@smash-sdk/billing/01-2022";
import { config, Region } from '@smash-sdk/core';
import { Directory } from "@smash-sdk/directory/10-2019";
import { Iam } from "@smash-sdk/iam/10-2019";
import moment from 'moment';
import { getDecodedToken, isApiPlan } from "../../../shared/helpers/helpers";
import { DisplayUser } from "../models/DisplayUser.interface";
import { LoginUser } from "../models/LoginUser.interface";
import { SignupUser } from "../models/SignupUser.interface";
import cookieService from "./cookie.service";


const hasActiveSession = (): boolean => {
    const cookieToken = cookieService.get(process.env.REACT_APP_TOKEN_NAME as string);
    if (cookieToken) {
        config.setToken(cookieToken);
        const decodedToken = getDecodedToken();
        if (decodedToken.username) {
            return true;
        }
    }
    return false;
};

const createAnonymousUser = async (): Promise<{ identity: DisplayUser | null }> => {
    try {
        const iamSdk = new Iam();
        const { account } = await iamSdk.createAccount();
        setTokens(account);
        return { identity: account }
    } catch (error) {
        throw error;
    }
}

const signup = async (userSignup: SignupUser): Promise<{ identity: DisplayUser | null, subscription: CreateSubscriptionOutput['subscription'] | null, plan: GetPlanOutput['plan'] | null }> => {
    try {
        const iamSdk = new Iam();
        const { account } = await iamSdk.createAccount({ ...userSignup });
        const directory = new Directory();
        const { identity } = await directory.createToken({ ...userSignup });

        if (account && identity) {
            setTokens(identity);
            const billing = new Billing();
            const { group } = await billing.getGroup({ groupId: process.env.REACT_APP_SMASH_PLAN_FREE_TRIAL_GROUP_ID as string });
            if (!group?.plans?.length) {
                throw new Error(`No plan found for groupId ${process.env.REACT_APP_SMASH_PLAN_FREE_TRIAL_GROUP_ID}`);
            }
            const freeTrialPlan = group.plans[0];
            const { plan } = await billing.getPlan({ planId: freeTrialPlan.id });
            const { subscription } = await billing.createSubscription({ plan: freeTrialPlan.id });
            return { identity: { ...identity }, subscription, plan };
        }
        return { identity: null, subscription: null, plan: null }
    } catch (error) {
        logout();
        throw error
    }
}

const login = async (userLogin: LoginUser): Promise<{ identity: DisplayUser | null }> => {
    try {
        const directory = new Directory();
        const { identity } = await directory.createToken({ ...userLogin });

        config.setToken(identity.token.token);
        config.setRegion(identity.region as Region);
        const billing = new Billing();
        const { subscription } = await billing.getSubscription();

        if (!isApiPlan(subscription.plan)) {
            throw new Error(`notCompatible`);
        }

        if (identity?.region) {
            setTokens(identity);
            return { identity: { ...identity } };
        }
        return { identity: null }
    } catch (error) {
        logout();
        throw error;
    }
}

const logout = (): void => {
    localStorage.removeItem('identity');
    cookieService.remove(process.env.REACT_APP_TOKEN_NAME as string);
    cookieService.remove(process.env.REACT_APP_REFRESH_TOKEN_NAME as string);
}

const verifyJwt = async (): Promise<boolean> => {
    const identity = localStorage.getItem('identity');
    if (identity) {
        const parsedIdentity = JSON.parse(identity) as DisplayUser;
        const { username, token, refreshToken } = parsedIdentity;
        if (username && token && refreshToken) {
            setTokens(parsedIdentity);
            return true;
        }
    }
    return false;
}

const setTokens = (identity: DisplayUser) => {//TODO FIX ME rename function setIdentity
    const { token, refreshToken } = identity;
    cookieService.set(process.env.REACT_APP_TOKEN_NAME as string, token.token, { expires: moment(token.expiration).toDate() });
    cookieService.set(process.env.REACT_APP_REFRESH_TOKEN_NAME as string, refreshToken.token, { expires: moment(refreshToken.expiration).toDate() });
    localStorage.setItem('identity', JSON.stringify(identity));
    config.setToken(token.token);
}

const authService = {
    hasActiveSession,
    createAnonymousUser,
    signup,
    login,
    logout,
    verifyJwt,
    setTokens,
}

export default authService;